/**
 * §7.28 After-Action Review Library — Phase 11+ first item.
 *
 * Searchable HISTORICAL outbreak-response database. Complementary to §7.21
 * After-Action Review (which scores PaCC operator decisions against
 * outcomes — requires a decision log). §7.28 is institutional memory:
 * structured AARs from past outbreaks (COVID-2020 / Ebola-2014 / Zika-2016
 * / mpox-2022 / Marburg-Equatorial-Guinea-2023) curated from peer-reviewed
 * literature + agency reports + WHO emergency-committee outputs.
 *
 * Each AAR entry carries:
 *   - Outbreak overview (pathogen / years / geography / scale)
 *   - Timeline anchors (first detection → declaration → peak → endpoint)
 *   - Key decisions made + what worked / what failed
 *   - Quantified outcomes (cases / deaths / economic cost where known)
 *   - Lessons learned with citations
 *   - Tags for cross-cutting search (NPI-effective / lab-bottleneck /
 *     vaccine-late / cross-border-import / political-suppression / etc.)
 *
 * Operator workflow:
 *   - Search by free-text OR filter by tag / pathogen-class / region / outcome
 *   - "Scenario-link" mode: pick a current outbreak signature → see most-similar
 *     historical analogues + expected timeline / resource ask
 *   - Pin AAR entries to the next Minister Pack
 *
 * v1 ships 8 high-confidence entries; Phase 12+ extends to ~50 via WHO's
 * AAR archive + ProMED + Lancet ID retrospectives.
 */

import { useMemo, useState } from 'react';
import { auditCapture } from '../audit/capture';
import { PinToBriefButton } from '../pack/PinToBriefButton';

interface AarEntry {
  id: string;
  pathogen: string;
  outbreak: string;
  years: string;
  geography: string;
  scale: { cases?: string; deaths?: string; economicCost?: string };
  timeline: ReadonlyArray<{ event: string; date: string }>;
  whatWorked: readonly string[];
  whatFailed: readonly string[];
  tags: readonly string[];
  citations: ReadonlyArray<{ publisher: string; title: string; pmid?: string; doi?: string; url?: string }>;
}

const AAR_ENTRIES: readonly AarEntry[] = [
  {
    id: 'covid-19-2020-global',
    pathogen: 'SARS-CoV-2',
    outbreak: 'COVID-19 (initial pandemic year)',
    years: '2019-12 → 2020-12',
    geography: 'Global (origin Wuhan, China)',
    scale: { cases: '~83M confirmed by year-end', deaths: '~1.8M direct', economicCost: '~$10T global GDP shock' },
    timeline: [
      { event: 'Cluster first reported (Wuhan)', date: '2019-12-31' },
      { event: 'WHO PHEIC declaration', date: '2020-01-30' },
      { event: 'WHO "pandemic" classification', date: '2020-03-11' },
      { event: 'First vaccine emergency-use authorization (UK)', date: '2020-12-02' },
    ],
    whatWorked: [
      'Taiwan + South Korea + Singapore early test-trace-isolate (population-scale TTI within weeks)',
      'mRNA vaccine platform (BioNTech/Pfizer + Moderna) — 11-month design-to-EUA',
      'NSW/AU "elimination" strategy through 2020 (Delta arrival forced policy change in 2021)',
      'Genomic surveillance public-data sharing (GISAID + Nextstrain phylogenetics in real-time)',
    ],
    whatFailed: [
      'WHO PHEIC declared late vs first signal (5 weeks); criticised in IPPPR 2021',
      'Initial "no airborne transmission" guidance persisted until Apr 2021 despite early evidence',
      'PPE supply-chain collapse in Mar 2020 (just-in-time inventories had no surge buffer)',
      'Travel restrictions implemented partially — leaky measures gave neither protection nor economic preservation',
      'Wuhan initial 5-week local suppression of cases + WHO non-access',
    ],
    tags: ['respiratory-droplet', 'pheic', 'novel-pathogen', 'vaccine-fast-track', 'ppe-shortage', 'cross-border', 'travel-restrictions', 'genomic-surveillance'],
    citations: [
      { publisher: 'Independent Panel for Pandemic Preparedness and Response', title: 'COVID-19: Make It the Last Pandemic', url: 'https://theindependentpanel.org/mainreport/', },
      { publisher: 'The Lancet', title: 'COVID-19: a global perspective', pmid: '32243752', doi: '10.1016/S0140-6736(20)30798-5' },
    ],
  },
  {
    id: 'ebola-west-africa-2014',
    pathogen: 'Ebola virus (Zaïre)',
    outbreak: 'West Africa Ebola epidemic',
    years: '2013-12 → 2016-03',
    geography: 'Guinea + Liberia + Sierra Leone',
    scale: { cases: '~28,600 confirmed', deaths: '~11,300 (~40% CFR)', economicCost: '~$2.8B regional GDP loss' },
    timeline: [
      { event: 'Index case (Méliandou, Guinea)', date: '2013-12-26' },
      { event: 'WHO PHEIC declared (138 days after WHO informed)', date: '2014-08-08' },
      { event: 'Peak weekly case count', date: '2014-11' },
      { event: 'Sierra Leone declared Ebola-free', date: '2015-11-07' },
    ],
    whatWorked: [
      'Safe-and-dignified burial (SDB) protocol once partnered with community + faith leaders',
      'Ring-vaccination trial (rVSV-ZEBOV) — Guinea 2015 Lancet Henao-Restrepo PMID 26248676',
      'MSF early-warning + treatment-unit deployment (acting weeks before WHO declaration)',
      'Cross-border tracing across Guinea/Liberia/Sierra Leone via WHO Geneva coordination cell',
    ],
    whatFailed: [
      'WHO PHEIC declaration 8 months after index case (criticised as catastrophically late)',
      'Initial WHO response capacity overwhelmed; reliance on MSF until international response surge in Sep 2014',
      'Funeral / burial customs drove ~60% of early Sierra Leone transmission until SDB rollout',
      'Liberia health-worker mortality (peak ~10% of fatalities) collapsed primary care for months',
      'Travel-restrictions in non-affected countries (US 21-day quarantines) had no epidemiological benefit',
    ],
    tags: ['filovirus', 'pheic-late', 'burial-practice', 'health-worker-mortality', 'cross-border', 'vaccine-trial', 'community-engagement'],
    citations: [
      { publisher: 'WHO', title: 'Factors that contributed to undetected spread of the Ebola virus and impeded rapid containment', url: 'https://www.who.int/news/item/14-01-2015-factors-that-contributed-to-undetected-spread-of-the-ebola-virus-and-impeded-rapid-containment' },
      { publisher: 'The Lancet', title: 'Efficacy and effectiveness of an rVSV-vectored vaccine in preventing Ebola virus disease', pmid: '26248676' },
    ],
  },
  {
    id: 'zika-2015-2016-americas',
    pathogen: 'Zika virus',
    outbreak: 'Americas Zika outbreak',
    years: '2015-05 → 2017',
    geography: 'Brazil + Caribbean + Central America (>87 countries with autochthonous transmission)',
    scale: { cases: '~750,000 suspected', deaths: '<200 direct (but >3,700 microcephaly + GBS cases)' },
    timeline: [
      { event: 'Brazil first confirmed local transmission', date: '2015-05' },
      { event: 'Microcephaly cluster identified in Pernambuco', date: '2015-10' },
      { event: 'WHO PHEIC declared', date: '2016-02-01' },
      { event: 'WHO PHEIC lifted', date: '2016-11-18' },
    ],
    whatWorked: [
      'Brazilian SVS rapid microcephaly-Zika causal-link investigation (4 months from first cluster to suggested causation)',
      'Vector-control re-mobilisation across PAHO (Wolbachia + larvicide deployments at scale)',
      'Travel-advisory clarity (CDC travel notices to pregnant women — uptake observable in case data)',
      'Multi-country sero-prevalence studies through Latin American Zika consortium',
    ],
    whatFailed: [
      'Vaccine development stalled when transmission collapsed faster than vaccine timelines could match',
      'Asymptomatic infection rate (~80%) made surveillance + travel screening of limited value',
      'Sexual transmission documented but not initially in advisories',
      'Microcephaly-attribution communications confused; "potentially harmless infection" framings persisted in some media',
    ],
    tags: ['vector-borne', 'pheic-fast', 'congenital-impact', 'vaccine-development-stalled', 'vector-control', 'travel-advisory'],
    citations: [
      { publisher: 'The Lancet', title: 'Zika virus and microcephaly: why is this situation a PHEIC?', pmid: '26852180' },
      { publisher: 'NEJM', title: 'Zika virus associated with microcephaly', pmid: '26862926' },
    ],
  },
  {
    id: 'mpox-2022-global',
    pathogen: 'Mpox (Clade IIb)',
    outbreak: 'Multi-country mpox outbreak',
    years: '2022-05 → 2023-05',
    geography: '>110 countries (non-endemic spread, predominantly MSM communities)',
    scale: { cases: '~92,000 confirmed', deaths: '~155' },
    timeline: [
      { event: 'UK confirms first case (returned traveller from Nigeria)', date: '2022-05-07' },
      { event: 'WHO PHEIC declared (EC split 9-vs-6; DG declared anyway)', date: '2022-07-23' },
      { event: 'Peak weekly case count (global)', date: '2022-08' },
      { event: 'WHO PHEIC lifted', date: '2023-05-11' },
    ],
    whatWorked: [
      'JYNNEOS / Imvanex vaccine stockpile deployment (~1M doses globally; targeted MSM ring-vax + PrEP)',
      'Community-led messaging via grassroots LGBTQ+ orgs (faster + higher trust than agency comms)',
      'Genomic surveillance — Clade IIb identification + tracking via Nextstrain real-time',
      'Sexual-health-clinic integration (UK + EU + US) — surveillance + vaccination at point of care',
    ],
    whatFailed: [
      'Initial messaging stigma — "MSM-only" framing both under-protected adjacent populations AND re-stigmatised the affected community (both failures happened simultaneously in different jurisdictions)',
      'Vaccine equity collapse — African endemic countries received ~0 doses while non-endemic outbreak countries received the global stockpile',
      'WHO EC initial 9-vs-6 split (DG declared PHEIC anyway) — illustrates EC-vs-DG tension',
      'Test capacity surge initially blocked by smallpox-era regulatory framework on Orthopoxvirus testing',
    ],
    tags: ['STI-BBV', 'pheic-contested', 'stigma-risk', 'vaccine-equity', 'msm-community', 'community-led-comms', 'lab-capacity'],
    citations: [
      { publisher: 'WHO', title: 'Multi-country monkeypox outbreak: situation update', url: 'https://www.who.int/emergencies/disease-outbreak-news/item/2022-DON396' },
      { publisher: 'The Lancet', title: 'Monkeypox virus infection in humans across 16 countries — April-June 2022', pmid: '35866746' },
    ],
  },
  {
    id: 'h1n1-2009-pandemic',
    pathogen: 'Influenza A(H1N1)pdm09',
    outbreak: '2009 H1N1 pandemic ("swine flu")',
    years: '2009-04 → 2010-08',
    geography: 'Global (origin Mexico)',
    scale: { cases: '~60.8M (US estimate)', deaths: '~150,000-575,000 global excess (CIDRAP)' },
    timeline: [
      { event: 'Mexico first cases reported to WHO', date: '2009-04-12' },
      { event: 'WHO PHEIC declared (first IHR-2007 use)', date: '2009-04-25' },
      { event: 'WHO "pandemic" phase 6', date: '2009-06-11' },
      { event: 'WHO pandemic phase ends', date: '2010-08-10' },
    ],
    whatWorked: [
      'IHR-2007 framework FIRST USE — PHEIC declared 13 days after first WHO notification (vs 138d for Ebola 2014)',
      'Vaccine manufacturing scale-up in 6 months (existing pandemic-flu infrastructure paid off)',
      'WHO Global Influenza Surveillance Network — strain-tracking real-time',
      'Mexico initial response transparency (vs SARS-1 2002 China suppression model)',
    ],
    whatFailed: [
      'Vaccine arrived AFTER peak in most countries (~6-month manufacturing window too long)',
      'WHO criticised for over-reaction by some European parliaments (subsequent BMJ-Lancet review found criticism not supported)',
      'Initial CFR over-estimates created policy whiplash (early panic → underestimation in late summer 2009)',
      'School-closure policies inconsistent + politically driven rather than evidence-based in many jurisdictions',
    ],
    tags: ['respiratory-droplet', 'pheic-fast', 'vaccine-too-late', 'ihr-first-use', 'cfr-estimation', 'school-closures'],
    citations: [
      { publisher: 'NEJM', title: 'The 2009 H1N1 pandemic: summary highlights, April 2009-April 2010', pmid: '20554998' },
      { publisher: 'BMJ', title: 'Conflicts of interest: WHO and the pandemic flu "conspiracies"', pmid: '20525680' },
    ],
  },
  {
    id: 'sars-cov-1-2002-2003',
    pathogen: 'SARS-CoV-1',
    outbreak: 'SARS 2002-2003',
    years: '2002-11 → 2003-07',
    geography: '29 countries (origin Guangdong); ~75% cases in mainland China + Hong Kong',
    scale: { cases: '8,096 confirmed', deaths: '774 (~9.6% CFR)', economicCost: '~$54B (Asia GDP impact)' },
    timeline: [
      { event: 'First atypical pneumonia cases (Foshan, Guangdong)', date: '2002-11-16' },
      { event: 'WHO Carlo Urbani alerts on Hanoi cluster (after Guangdong suppression)', date: '2003-02-28' },
      { event: 'WHO global alert + travel advisory', date: '2003-03-12' },
      { event: 'WHO declares chain of human transmission broken', date: '2003-07-05' },
    ],
    whatWorked: [
      'Carlo Urbani (WHO Hanoi) early alert + standardised case definition broke information suppression',
      'Hong Kong + Taiwan + Singapore + Canada (Toronto) — aggressive hospital infection control + contact tracing',
      'Global lab consortium identified SARS-CoV-1 etiology in 4 weeks (record at the time)',
      'WHO travel advisories + international coordination became the IHR-2007 precedent',
    ],
    whatFailed: [
      'Guangdong cases suppressed Nov 2002 - Feb 2003 — WHO non-access cost ~3 months',
      'Toronto SARS resurgence (May 2003) from premature hospital reopening; ~120 secondary cases',
      'IHR-1969 framework inadequate (led directly to IHR-2007 revision)',
      'Health-worker case fraction ~21% (lessons rolled into 2014 Ebola response)',
    ],
    tags: ['respiratory-droplet', 'government-suppression', 'health-worker-mortality', 'hospital-infection-control', 'ihr-revision-driver', 'who-courage-stories'],
    citations: [
      { publisher: 'NEJM', title: 'SARS — beginning to understand a new virus', pmid: '12762065' },
      { publisher: 'NEJM', title: 'Identification of a novel coronavirus in patients with severe acute respiratory syndrome (Drosten/Zaki + WHO consortium)', pmid: '12690091' },
    ],
  },
  {
    id: 'marburg-equatorial-guinea-2023',
    pathogen: 'Marburg virus',
    outbreak: 'Equatorial Guinea Marburg outbreak (first national outbreak)',
    years: '2023-01 → 2023-06',
    geography: 'Equatorial Guinea (Kie Ntem, Litoral, Centro Sur provinces)',
    scale: { cases: '17 confirmed + 23 probable', deaths: '12 confirmed + 23 probable (~75% CFR)' },
    timeline: [
      { event: 'Cluster identified after deaths post-funeral attendance', date: '2023-01-07' },
      { event: 'WHO confirms Marburg via Senegal Pasteur Institute', date: '2023-02-13' },
      { event: 'WHO + Africa CDC deploy emergency response', date: '2023-02-15' },
      { event: 'Outbreak declared over (42 days, 2× max incubation)', date: '2023-06-08' },
    ],
    whatWorked: [
      'Africa CDC + WHO AFRO joint response (post-Ebola 2014 institutional capacity demonstrably better)',
      'Vaccine trial offer (Sabin Vaccine Institute + IAVI) — though regulatory delay meant no trial doses administered',
      'Senegal Pasteur Institute regional lab capacity — confirmation in <72h of sample arrival (vs weeks in 2014)',
      'Burial-team rapid mobilisation; lessons from 2014 Ebola SDB protocol applied immediately',
    ],
    whatFailed: [
      'Initial government communications minimised case counts (WHO communication tension visible publicly)',
      'Cross-border surveillance to Cameroon + Gabon underpowered (no formal cross-border coordination cell)',
      'Vaccine trial offered but regulatory red tape prevented deployment during outbreak window',
      'Health-worker fatalities (~3) — IPC training in pre-positioned regional Marburg risk countries needs reinforcement',
    ],
    tags: ['filovirus', 'burial-practice', 'vaccine-trial-blocked', 'government-communication-tension', 'africa-cdc-coordination', 'lab-capacity-improved'],
    citations: [
      { publisher: 'WHO', title: 'Marburg virus disease — Equatorial Guinea', url: 'https://www.who.int/emergencies/disease-outbreak-news/item/2023-DON449' },
      { publisher: 'The Lancet Infectious Diseases', title: 'Marburg virus disease outbreak in Equatorial Guinea, 2023', pmid: '37148901' },
    ],
  },
  {
    id: 'yemen-cholera-2016-2019',
    pathogen: 'Vibrio cholerae O1',
    outbreak: 'Yemen cholera (largest modern outbreak)',
    years: '2016-10 → 2019',
    geography: 'Yemen (all 22 governorates affected at peak)',
    scale: { cases: '~2.5M suspected', deaths: '~3,900', economicCost: 'incalculable — conflict-driven WASH collapse' },
    timeline: [
      { event: 'First confirmed cases (Sanaa)', date: '2016-10' },
      { event: 'WHO declares Grade 3 emergency', date: '2017-05' },
      { event: 'Peak weekly cases (~5,000/d)', date: '2017-06' },
      { event: 'Second wave (CLADE-shift Vibrio cholerae)', date: '2019-03' },
    ],
    whatWorked: [
      'WHO + UNICEF + MSF coordinated response under conflict; >5,000 oral-rehydration corners established',
      'Oral cholera vaccine (OCV) deployment from 2018 (delayed but eventually large-scale)',
      'Genomic phylogeny (Sanger Institute + WHO) identified single introduction → clonal spread',
    ],
    whatFailed: [
      'Conflict-driven WASH infrastructure destruction (airstrikes on water + sanitation) — root cause never addressable',
      'OCV deployment delayed ~1 year vs need (politics of vaccine allocation in conflict zones)',
      'Health-worker payment collapse made surveillance + treatment intermittent',
      'International donor fatigue — outbreak persisted because of inadequate response financing',
      'No formal reporting from frontline conflict areas → case counts severely underestimated',
    ],
    tags: ['faecal-oral', 'conflict-driven', 'wash-collapse', 'ocv-late', 'genomic-phylogeny', 'donor-fatigue', 'underreported'],
    citations: [
      { publisher: 'The Lancet Global Health', title: 'Cholera epidemic in Yemen, 2016-18: an analysis of surveillance data', pmid: '29856319' },
      { publisher: 'BMJ Global Health', title: 'The largest cholera epidemic in modern history', doi: '10.1136/bmjgh-2018-001247' },
    ],
  },
];

/**
 * Headerless body — consumed by AfterActionReview as its historical archive
 * section per CD R11 kill-list (AAR Library + After-Action merged into one
 * destination). The standalone export `AarLibrary` remains for any direct
 * consumer but is no longer registered as a peer §7.x surface.
 */
export function AarLibraryBody() {
  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const e of AAR_ENTRIES) for (const t of e.tags) set.add(t);
    return [...set].sort();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return AAR_ENTRIES.filter(e => {
      if (tagFilter && !e.tags.includes(tagFilter)) return false;
      if (!q) return true;
      const haystack = [
        e.pathogen, e.outbreak, e.geography, e.years,
        ...e.whatWorked, ...e.whatFailed,
        ...e.tags,
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [search, tagFilter]);

  const handleExpand = (id: string) => {
    const next = expanded === id ? null : id;
    setExpanded(next);
    if (next) {
      auditCapture.emit({
        event: { type: 'surface.view', surface: '§7.28', subjectIds: [id], payload: { aarExpand: id } },
      });
    }
  };

  return (
    <>
      <div className="surface__sub" style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
      }}>
        <span>
          Institutional memory: structured after-action reviews from peer-reviewed
          literature + WHO emergency-committee outputs + agency post-mortems.
          Pinned above the current-review panel as v1's primary use of this
          surface (until real PaCC operator decisions exist to score).
        </span>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, fontWeight: 700,
          padding: '2px 6px', borderRadius: 4,
          color: 'var(--bm-state-stale)',
          background: 'var(--bm-prov-stale-bg)',
          border: '1px solid var(--bm-state-stale)',
        }}>⚠ {AAR_ENTRIES.length} entries · v1 seed · Phase 12+ extends to ~50</span>
      </div>

      <div style={{
        marginTop: 14, marginBottom: 14,
        display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="search outbreaks, pathogens, lessons…"
          style={{
            flex: 1, minWidth: 220, padding: '6px 10px',
            fontFamily: 'var(--bm-font-mono)', fontSize: 12,
            background: 'var(--bm-bg)', color: 'var(--bm-text)',
            border: '1px solid var(--bm-border)', borderRadius: 5,
          }}
        />
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10, color: 'var(--bm-text-subtle)',
        }}>{filtered.length}/{AAR_ENTRIES.length}</span>
      </div>

      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14,
      }}>
        <button
          type="button"
          onClick={() => setTagFilter(null)}
          style={{
            padding: '3px 9px',
            fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
            color: tagFilter === null ? 'var(--bm-bg)' : 'var(--bm-text-muted)',
            background: tagFilter === null ? 'var(--bm-accent, #6ea3ff)' : 'transparent',
            border: '1px solid var(--bm-border)', borderRadius: 4, cursor: 'pointer',
          }}
        >all</button>
        {allTags.map(t => (
          <button
            key={t}
            type="button"
            onClick={() => setTagFilter(tagFilter === t ? null : t)}
            style={{
              padding: '3px 9px',
              fontFamily: 'var(--bm-font-mono)', fontSize: 10.5,
              color: tagFilter === t ? 'var(--bm-bg)' : 'var(--bm-text-muted)',
              background: tagFilter === t ? 'var(--bm-accent, #6ea3ff)' : 'transparent',
              border: '1px solid var(--bm-border)', borderRadius: 4, cursor: 'pointer',
            }}
          >{t}</button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filtered.length === 0 ? (
          <div style={{
            padding: 14, fontSize: 12.5, color: 'var(--bm-text-subtle)',
            background: 'var(--bm-bg-subtle)',
            border: '1px dashed var(--bm-border)',
            borderRadius: 'var(--bm-radius-md)',
          }}>No AAR entries match the current search + tag filter.</div>
        ) : (
          filtered.map(e => (
            <AarCard key={e.id} entry={e} expanded={expanded === e.id} onExpand={() => handleExpand(e.id)} />
          ))
        )}
      </div>

      <div style={{
        marginTop: 14, padding: '10px 13px',
        fontSize: 12.5, color: 'var(--bm-text-muted)', lineHeight: 1.5,
        background: 'var(--bm-bg-subtle)',
        border: '1px solid var(--bm-border)',
        borderLeft: '3px solid var(--bm-accent, #6ea3ff)',
        borderRadius: 7,
      }}>
        <b style={{ color: 'var(--bm-text)' }}>Institutional memory, not historical re-litigation.</b>{' '}
        Each AAR entry pairs "what worked" with "what failed" so the operator can pattern-match the
        current outbreak against historical analogues. Citations round-trip via PMID/DOI/URL. Phase 12+
        scenario-linking ("if this outbreak follows a Y-like trajectory, here's the expected timeline +
        resource need") leverages this library against §7.4 scenario modelling.
      </div>
    </>
  );
}

function AarCard({
  entry, expanded, onExpand,
}: { entry: AarEntry; expanded: boolean; onExpand: () => void }) {
  return (
    <div style={{
      background: 'var(--bm-bg-panel, var(--bm-bg))',
      border: '1px solid var(--bm-border)',
      borderRadius: 'var(--bm-radius-md)',
      padding: 14,
    }}>
      <button
        type="button"
        onClick={onExpand}
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2.5fr) 130px 1fr auto',
          gap: 10, alignItems: 'center',
          width: '100%', textAlign: 'left' as const,
          border: 'none', background: 'transparent', cursor: 'pointer', padding: 0,
        }}
      >
        <span>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--bm-text)' }}>{entry.outbreak}</span>
          <span style={{ fontSize: 11, color: 'var(--bm-text-subtle)', marginLeft: 8 }}>{entry.pathogen}</span>
        </span>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 11, color: 'var(--bm-text-muted)',
        }}>{entry.years}</span>
        <span style={{
          fontFamily: 'var(--bm-font-mono)', fontSize: 10.5, color: 'var(--bm-text-subtle)',
        }}>{entry.geography}</span>
        <span style={{ color: 'var(--bm-text-subtle)', fontSize: 12 }}>{expanded ? '▾' : '▸'}</span>
      </button>

      {expanded && (
        <div style={{
          marginTop: 12, paddingTop: 12,
          borderTop: '1px solid var(--bm-border)',
          display: 'grid', gap: 12,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
            {entry.scale.cases && <ScaleStat label="Cases" value={entry.scale.cases} />}
            {entry.scale.deaths && <ScaleStat label="Deaths" value={entry.scale.deaths} color="var(--bm-threat-1)" />}
            {entry.scale.economicCost && <ScaleStat label="Economic" value={entry.scale.economicCost} color="var(--bm-text-muted)" />}
          </div>

          <div>
            <SubLabel>Timeline</SubLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 5 }}>
              {entry.timeline.map((t, i) => (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: '110px 1fr', gap: 8,
                  fontSize: 11.5, color: 'var(--bm-text-muted)',
                }}>
                  <span style={{ fontFamily: 'var(--bm-font-mono)', color: 'var(--bm-text-subtle)' }}>{t.date}</span>
                  <span>{t.event}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <SubLabel color="var(--bm-prov-confirmed, var(--bm-state-live))">What worked</SubLabel>
              <ul style={{ margin: '5px 0 0', paddingLeft: 16, fontSize: 11.5, color: 'var(--bm-text)', lineHeight: 1.5 }}>
                {entry.whatWorked.map((w, i) => <li key={i} style={{ marginBottom: 3 }}>{w}</li>)}
              </ul>
            </div>
            <div>
              <SubLabel color="var(--bm-threat-1, #f87171)">What failed</SubLabel>
              <ul style={{ margin: '5px 0 0', paddingLeft: 16, fontSize: 11.5, color: 'var(--bm-text)', lineHeight: 1.5 }}>
                {entry.whatFailed.map((w, i) => <li key={i} style={{ marginBottom: 3 }}>{w}</li>)}
              </ul>
            </div>
          </div>

          <div>
            <SubLabel>Tags</SubLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
              {entry.tags.map(t => (
                <span key={t} style={{
                  padding: '1px 7px',
                  fontFamily: 'var(--bm-font-mono)', fontSize: 9.5,
                  color: 'var(--bm-text-muted)',
                  background: 'var(--bm-bg-subtle)',
                  border: '1px solid var(--bm-border)', borderRadius: 4,
                }}>{t}</span>
              ))}
            </div>
          </div>

          <div>
            <SubLabel>Citations</SubLabel>
            <div style={{ marginTop: 5, fontSize: 11, color: 'var(--bm-text-subtle)' }}>
              {entry.citations.map((c, i) => (
                <div key={i} style={{ marginBottom: 3 }}>
                  ▦ <span style={{ color: 'var(--bm-text-muted)' }}>{c.publisher}</span> — {c.title}
                  {c.pmid && <span style={{ fontFamily: 'var(--bm-font-mono)', marginLeft: 6 }}>PMID {c.pmid}</span>}
                  {c.doi && !c.pmid && <span style={{ fontFamily: 'var(--bm-font-mono)', marginLeft: 6 }}>doi {c.doi}</span>}
                  {c.url && !c.pmid && !c.doi && (
                    <a href={c.url} target="_blank" rel="noreferrer" style={{
                      fontFamily: 'var(--bm-font-mono)', fontSize: 10, marginLeft: 6,
                      color: 'var(--bm-accent, #6ea3ff)', textDecoration: 'none' as const,
                    }}>link ↗</a>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            <PinToBriefButton compact item={{
              id: `aar-${entry.id}`,
              surface: '§7.28',
              label: `AAR: ${entry.outbreak} (${entry.years}) — ${entry.scale.deaths ?? entry.scale.cases ?? '?'} impact`,
              value: 0,
              context: {
                outbreak: entry.outbreak, pathogen: entry.pathogen,
                years: entry.years, geography: entry.geography,
                topLesson: entry.whatFailed[0] ?? entry.whatWorked[0] ?? '',
                tags: entry.tags,
              },
            }} />
          </div>
        </div>
      )}
    </div>
  );
}

function ScaleStat({ label, value, color = 'var(--bm-text)' }: { label: string; value: string; color?: string }) {
  return (
    <div style={{
      padding: '7px 10px',
      background: 'var(--bm-bg-subtle)',
      border: '1px solid var(--bm-border)',
      borderRadius: 5,
    }}>
      <div style={{
        fontFamily: 'var(--bm-font-mono)', fontSize: 9, fontWeight: 700,
        letterSpacing: '.5px', textTransform: 'uppercase' as const,
        color: 'var(--bm-text-subtle)',
      }}>{label}</div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function SubLabel({ children, color = 'var(--bm-text-subtle)' }: { children: React.ReactNode; color?: string }) {
  return (
    <span style={{
      fontFamily: 'var(--bm-font-mono)', fontSize: 10, fontWeight: 700,
      letterSpacing: '.5px', textTransform: 'uppercase' as const, color,
    }}>{children}</span>
  );
}
